import type { ReactNode } from "react"
import { useCallback, useEffect, useState } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { AnimatePresence, motion } from "motion/react"

import { MembershipRole } from "@productlane/db/browser"

import { AnimateChangeInHeight } from "@/components/animate-change-in-height"
import { MetaInfo } from "@/components/meta-info"
import { minDelay } from "@/lib"
import { api } from "@/lib/api"
import { useSetupWorkspace } from "@/lib/states/setup-workspace-state"
import { ConnectDocsSlide } from "./connect-docs-slide"
import { ConnectMcpSlide } from "./connect-mcp-slide"
import { EditWorkspaceNameSlide } from "./edit-workspace-name-slide"
import { GetStartedSlide } from "./get-started-slide"
import { InviteSlide } from "./invite-slide"
import { LoadingSlide } from "./loading-slide"
import { ReadySlide } from "./ready-slide"
import { SlideWrapper } from "./slide-wrapper"
import { UseCasesSlide } from "./use-cases-slide"
import { useSetupWorkspacePin } from "./use-setup-workspace-pin"
import { WelcomeSlide } from "./welcome-slide"

export function SetupWorkspaceScreen() {
	const { step, setStep, completeSetup, startSetup, isSettingUpWorkspace } =
		useSetupWorkspace()
	const { pin, isSwitchingBack } = useSetupWorkspacePin(step)
	const leaveWorkspaceStep = async () => {
		await pin()
		setStep(2)
	}
	const navigate = useNavigate()
	const [isLoading, setIsLoading] = useState(true)
	const [isInitialized, setIsInitialized] = useState(false)
	const [resumedFromOauth, setResumedFromOauth] = useState(false)

	const userQuery = api.user.get.useQuery(undefined, {
		retry: true,
		refetchOnWindowFocus: false,
		enabled: false,
	})
	const allowedDomains = api.user.allowedDomains.useQuery(undefined)
	// Fetched here so the create form knows about the domain switch on first paint.
	api.user.colleagueWorkspace.useQuery(undefined)

	// Fetch the workspaces that the user owns — always refetch on mount so we
	// get fresh data even on reload (staleTime alone would serve a stale cache).
	const ownedWorkspaces = api.user.listWorkspaces.useQuery(
		{
			role: MembershipRole.ADMIN,
		},
		{
			refetchOnMount: "always",
		},
	)

	const context = api.useUtils()

	const start = useCallback(async () => {
		// invalidate all queries
		await context.invalidate()
		await context.user.get.invalidate()
		await context.user.listWorkspaces.invalidate()
		await context.workspace.info.invalidate()
		await context.user.allowedDomains.invalidate()

		completeSetup()

		// Short: the reveal's veil covers the handover, so a longer gray gap is dead time.
		setTimeout(() => {
			void navigate({ to: "/threads" })
		}, 300)
	}, [context, completeSetup, navigate])

	const isNew = useSearch({ strict: false, select: (s) => s.new })
	const queryStep = useSearch({ strict: false, select: (s) => s.step })

	useEffect(() => {
		if (!isInitialized) {
			setIsInitialized(true)
		} else {
			return
		}

		// Ensure setup state is active whenever this screen mounts — covers
		// the case where the user reloaded and the persisted flag was lost.
		startSetup()

		async function load() {
			try {
				const fetchPromise = userQuery.refetch()
				// add some artificial delay to avoid just flashing the loading screen shortly
				await minDelay(fetchPromise, 1500)
			} finally {
				setIsLoading(false)
			}
		}

		// Respect an explicit `?step=` (e.g. Linear OAuth resumes at step 1).
		// Only reset to step 0 when no step was requested — clobbering unconditionally
		// sent Linear signups back through the create-workspace form and produced
		// duplicate workspaces.
		if (queryStep) {
			setStep(Number(queryStep))
			setResumedFromOauth(true)
		} else if (isNew) {
			setStep(0)
		} else {
			// Snap to step 0 when the user has no workspace yet, so a stale persisted
			// `step` from an abandoned prior attempt can't strand them mid-wizard.
			void ownedWorkspaces.refetch().then((result) => {
				if ((result.data?.length ?? 0) === 0) {
					setStep(0)
				}
			})
		}

		void load()

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isInitialized])

	// Only redirect away once we've confirmed the user actually has workspaces.
	// Checking isSettingUpWorkspace alone is unreliable because Zustand's
	// persisted state may not have hydrated yet when this component first mounts,
	// causing a redirect loop between /setup-workspace and /threads.
	// Also bail out when the user is mid-flow (step > 0) — by then they've already
	// passed the welcome screen and may have just created their first workspace,
	// so ownedWorkspaces.length > 0 is expected and is NOT a signal to redirect.
	useEffect(() => {
		if (isLoading) return
		if (isSettingUpWorkspace) return
		if (step > 0) return
		if (!ownedWorkspaces.isFetched) return
		if ((ownedWorkspaces.data?.length ?? 0) > 0) {
			void navigate({ to: "/threads" })
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		isLoading,
		isSettingUpWorkspace,
		step,
		ownedWorkspaces.isFetched,
		ownedWorkspaces.data,
	])

	const hasOwnedWorkspace = (ownedWorkspaces.data?.length ?? 0) > 0

	const content: Record<number, ReactNode> = {
		// welcome slide
		0: (
			<SlideWrapper key="welcome" initial={undefined}>
				<WelcomeSlide
					nextSlide={() => {
						setStep(1)
					}}
				/>
			</SlideWrapper>
		),
		// Workspace-name step. Only show the edit variant when we resumed onboarding
		// from Linear OAuth (which auto-created a workspace and routed here via
		// `?step=1`). For all other entries — including a returning admin clicking
		// "Create or join a workspace" — show the create/join tab so they can
		// actually create a new workspace instead of editing their existing one.
		1:
			hasOwnedWorkspace && resumedFromOauth ? (
				<SlideWrapper key="edit-workspace-name">
					<EditWorkspaceNameSlide nextSlide={() => void leaveWorkspaceStep()} />
				</SlideWrapper>
			) : (
				<SlideWrapper key="get-started">
					<GetStartedSlide
						// This is ok, because we first display the actual data when isLoading is false
						allowedDomains={allowedDomains.data ?? []}
						key="get-started"
						nextSlide={() => void leaveWorkspaceStep()}
					/>
				</SlideWrapper>
			),
		// invite users to your workspace
		2: (
			<SlideWrapper key="invite">
				<InviteSlide
					nextSlide={() => {
						setStep(3)
					}}
				/>
			</SlideWrapper>
		),
		// select use cases
		3: (
			<SlideWrapper key="use-cases">
				<UseCasesSlide
					nextSlide={() => {
						setStep(4)
					}}
				/>
			</SlideWrapper>
		),
		// import an existing help center
		4: (
			<SlideWrapper key="connect-docs">
				<ConnectDocsSlide
					nextSlide={() => {
						setStep(5)
					}}
				/>
			</SlideWrapper>
		),
		// connect MCP
		5: (
			<SlideWrapper key="connect-mcp">
				<ConnectMcpSlide
					nextSlide={() => {
						setStep(6)
					}}
				/>
			</SlideWrapper>
		),
		// confirm to go to the app
		6: (
			<SlideWrapper key="ready">
				<ReadySlide nextSlide={start} />
			</SlideWrapper>
		),
	}

	// Show the setup card when:
	// - actively setting up (isSettingUpWorkspace flag), OR
	// - the user genuinely has no workspaces yet (covers reloads where the flag was lost)
	const noWorkspaces =
		ownedWorkspaces.isFetched && (ownedWorkspaces.data?.length ?? 0) === 0
	const showCard = isSettingUpWorkspace || noWorkspaces

	return (
		<main className="relative flex h-dvh w-full flex-col items-center bg-gray-100 p-6 dark:bg-gray-900">
			<MetaInfo title="Setup Workspace" />
			<div className="flex w-full flex-1 items-center justify-center">
				<AnimatePresence>
					{showCard && (
						<motion.div
							className="relative z-40 w-full rounded-3xl bg-white p-8 shadow-2xl ring-1 ring-black/10 sm:max-w-2xl sm:p-14 dark:bg-gray-850 dark:shadow-black"
							initial={{ opacity: 0, transform: "translateY(10px)" }}
							animate={{ opacity: 1, transform: "translateY(0)" }}
							exit={{ opacity: 0, transform: "translateY(10px)" }}
						>
							<AnimateChangeInHeight>
								<AnimatePresence mode="wait">
									{allowedDomains.isLoading ||
									ownedWorkspaces.isLoading ||
									isLoading ||
									// Held until the session is back, so no step writes to the wrong workspace.
									isSwitchingBack ? (
										<SlideWrapper
											key="loading"
											// when we go to the welcome slide dont show the exit animation to make the icon transition properly
											exit={step === 0 ? undefined : { opacity: 0 }}
										>
											<LoadingSlide />
										</SlideWrapper>
									) : (
										content[step]
									)}
								</AnimatePresence>
							</AnimateChangeInHeight>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</main>
	)
}
